import {baseApi} from "./apiService";
import {Pagination} from "../types";

export interface CustomerAccount {
    id: string;
    email: string;
    first_name: string;
    last_name: string | null;
    phone: string | null;
    created_at: string;
    updated_at: string;
    // BE #31 — rebook-due signal (global, single boolean per the landed BE).
    // Computed from the customer's typical booking cadence across all shops.
    is_due_for_rebook?: boolean;
    average_interval_days?: number | null;
    last_booking_at?: string | null;
    // BE #51 — soft-pending deletion. Both ISO 8601 `Z`. Cleared together
    // when the user cancels via `PATCH /me {cancel_pending_deletion: true}`.
    pending_deletion_at?: string | null;
    scheduled_deletion_at?: string | null;
    // BE — email-verification flow. Set on confirm; null until verified.
    email_verified_at?: string | null;
    // Two-step email change: PATCH /me with a new email parks it here and
    // mails a 6-digit code to the NEW address; `email` keeps the login
    // identity until POST email-verification/confirm flips it. Re-submitting
    // the current email via PATCH cancels the change (→ null).
    pending_email?: string | null;
}

// BE #51 — DELETE /me success payload.
export interface DeleteCustomerMeResponse {
    scheduled_deletion_at: string
    grace_period_days: number
}

// BE #51 — POST /me/password payload.
export interface ChangePasswordPayload {
    current_password: string
    new_password: string
    new_password_confirm: string
}

// BE #31 — slot-hold contract. End time is computed server-side
// from the service duration, so the FE only sends the start.
export interface SlotHoldRequest {
    barber_shop_id: string;
    barber_id: string;
    service_id: string;
    start_date_time: string; // ISO 8601
}

export interface SlotHold {
    hold_id: string;
    expires_at: string; // ISO 8601 in shop timezone
}

export interface SlotHoldConflictError {
    error: 'slot_taken';
    reason: 'held_by_other' | 'booked';
}

export interface CustomerPortalReservation {
    id: string;
    service_title: string;
    title: string;
    duration: number;
    price: number;
    start_date_time: string;
    end_date_time: string;
    status: string;
    note: string | null;
    barber: {
        id: string;
        first_name: string;
        visible_name: string | null;
    } | null;
    barber_shop: {
        id: string;
        name: string;
        slug: string;
    };
    // Past confirmed visits: the score given (null = unrated). can_rate is true
    // only when the visit is unrated with a still-valid rating token.
    rating: number | null;
    can_rate: boolean;
    rate_token: string | null;
}

export interface CustomerPortalRating {
    id: string;
    score: number;
    comment: string | null;
    customer_name: string;
    created_at: string;
    barber: {
        id: string;
        first_name: string;
        visible_name: string | null;
    } | null;
}

export interface CustomerPortalStats {
    total_reservations: number;
    upcoming_reservations: number;
    total_reviews: number;
    barbershops_visited: number;
    total_spent_eur: number;
    avg_rating_given: number | null;
}

export interface FavoriteBarberShop {
    id: string;
    barber_shop: {
        id: string;
        name: string;
        slug: string;
        logo: string | null;
        primary_color: string | null;
        city: string | null;
        average_rating: number | null;
        review_count: number;
    };
    usual_service: string | null;   // most-booked service title at this shop
    visit_count: number;            // completed visits at this shop
    created_at: string;
}

// Rewards tab (BE pending — docs/CUSTOMER_PORTAL_REWARDS_BE.md)
export interface CustomerLoyaltyCard {
    shop_name: string;
    reward: string;        // e.g. "Free cut" / "20% off"
    every: number;         // stamps needed for the reward
    stamps: number;        // stamps collected
}

export interface CustomerGiftCardEntry {
    code: string;
    value: number;         // euros
    balance: number;       // euros remaining
    shop_name: string | null;   // null = global BarberMate card
    status: 'active' | 'redeemed';
}

export interface CustomerRewards {
    loyalty: CustomerLoyaltyCard[];
    gift_cards: CustomerGiftCardEntry[];
}

export interface CustomerPaymentMethod {
    id: string;
    brand: string;         // visa / mastercard / …
    last4: string;
    exp_month: number;
    exp_year: number;
    is_default: boolean;
}

// BE #49 — profile-edit payload. All fields optional so callers can patch
// only what changed. Notification prefs ride along; BE persists them onto
// the customer's notification profile. BE #51 added
// `cancel_pending_deletion` for clearing a scheduled deletion.
export interface UpdateCustomerMePayload {
    first_name?: string
    last_name?: string | null
    email?: string
    phone?: string | null
    email_reminders?: boolean
    sms_reminders?: boolean
    marketing_opt_in?: boolean
    cancel_pending_deletion?: boolean
}

export const customerPortalApi = baseApi.injectEndpoints({
    overrideExisting: false,
    endpoints: (builder) => ({
        getCustomerMe: builder.query<CustomerAccount, void>({
            query: () => ({
                url: 'customer/portal/me',
                method: 'GET',
            }),
            providesTags: ['CustomerPortal'],
        }),
        // BE #49 — update first/last name, email, phone, notification prefs.
        // Email is TWO-STEP: a new address lands in `pending_email` (code
        // mailed to it); the login email only flips on email-verification
        // confirm. Re-submitting the current address cancels a pending change.
        // 409 {error: email_exists} when the new address is already taken.
        updateCustomerMe: builder.mutation<CustomerAccount, UpdateCustomerMePayload>({
            query: (body) => ({
                url: 'customer/portal/me',
                method: 'PATCH',
                body,
            }),
            invalidatesTags: ['CustomerPortal'],
        }),
        // BE #51 — soft-pending account deletion with 14-day grace. Returns
        // schedule timestamps; FE shows them in a banner. All refresh tokens
        // get revoked server-side; the existing JWT stays valid until expiry.
        deleteCustomerMe: builder.mutation<DeleteCustomerMeResponse, void>({
            query: () => ({
                url: 'customer/portal/me',
                method: 'DELETE',
            }),
            invalidatesTags: ['CustomerPortal'],
        }),
        // BE #51 — change password. Returns a tiny `{changed: true}` ack.
        // Refresh tokens revoked server-side; current JWT keeps working.
        changeCustomerPassword: builder.mutation<{changed: true}, ChangePasswordPayload>({
            query: (body) => ({
                url: 'customer/portal/me/password',
                method: 'POST',
                body,
            }),
        }),
        getCustomerReservations: builder.query<Pagination<CustomerPortalReservation>, { page?: number; type?: 'all' | 'upcoming' | 'past' }>({
            query: ({page = 1, type = 'all'}) => ({
                url: `customer/portal/reservations?page=${page}&type=${type}`,
                method: 'GET',
            }),
            providesTags: ['CustomerPortalReservations'],
        }),
        getCustomerReviews: builder.query<Pagination<CustomerPortalRating>, { page?: number }>({
            query: ({page = 1}) => ({
                url: `customer/portal/reviews?page=${page}`,
                method: 'GET',
            }),
            providesTags: ['CustomerPortalReviews'],
        }),
        getCustomerStats: builder.query<CustomerPortalStats, void>({
            query: () => ({
                url: 'customer/portal/stats',
                method: 'GET',
            }),
            providesTags: ['CustomerPortalStats'],
        }),
        getCustomerFavorites: builder.query<FavoriteBarberShop[], void>({
            query: () => ({
                url: 'customer/portal/favorites',
                method: 'GET',
            }),
            providesTags: ['CustomerPortalFavorites'],
        }),
        // BE pending — see docs/CUSTOMER_PORTAL_REWARDS_BE.md.
        getCustomerRewards: builder.query<CustomerRewards, void>({
            query: () => ({url: 'customer/portal/rewards', method: 'GET'}),
        }),
        getCustomerPaymentMethods: builder.query<CustomerPaymentMethod[], void>({
            query: () => ({url: 'customer/portal/payment-methods', method: 'GET'}),
        }),
        addFavorite: builder.mutation<FavoriteBarberShop, string>({
            query: (barberShopId) => ({
                url: `customer/portal/favorites/${barberShopId}`,
                method: 'POST',
            }),
            invalidatesTags: ['CustomerPortalFavorites'],
        }),
        removeFavorite: builder.mutation<void, string>({
            query: (barberShopId) => ({
                url: `customer/portal/favorites/${barberShopId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['CustomerPortalFavorites'],
        }),
        // BE #31 — POST is idempotent on (customer_account, barber, start_date_time).
        // Re-calling extends the hold to now + holdMinutes and returns the same hold_id.
        createSlotHold: builder.mutation<SlotHold, SlotHoldRequest>({
            query: (body) => ({
                url: 'customer/portal/reservations/holds',
                method: 'POST',
                body,
            }),
        }),
        // BE #31 — explicit release on abandon. Server also auto-expires after holdMinutes.
        releaseSlotHold: builder.mutation<void, string>({
            query: (holdId) => ({
                url: `customer/portal/reservations/holds/${holdId}`,
                method: 'DELETE',
            }),
        }),
    }),
})

export const {
    useGetCustomerMeQuery,
    useUpdateCustomerMeMutation,
    useDeleteCustomerMeMutation,
    useChangeCustomerPasswordMutation,
    useGetCustomerReservationsQuery,
    useGetCustomerReviewsQuery,
    useGetCustomerStatsQuery,
    useGetCustomerFavoritesQuery,
    useAddFavoriteMutation,
    useRemoveFavoriteMutation,
    useCreateSlotHoldMutation,
    useReleaseSlotHoldMutation,
    useGetCustomerRewardsQuery,
    useGetCustomerPaymentMethodsQuery,
} = customerPortalApi
